/**
 * Population Structure Module Page (Redesigned - Korat Health Style)
 *
 * Detailed population demographics with age pyramid and district map
 */

import { useState } from 'react'
import {
  Users,
  Baby,
  Briefcase,
  User,
  GraduationCap,
  BookOpen,
  UserCheck,
  Heart,
  Activity,
  type LucideIcon,
} from 'lucide-react'
import {
  Sidebar,
  PageHeader,
  KpiCardRich,
  PopulationMapPolygon,
  DataTable,
  YearFilter,
  DistrictFilter,
  type Column,
} from '../shared/components'
import { LineChartCard } from '../shared/components/charts'
import { usePopulationStructure, usePopulationByDistrict, usePopulationTrends, useDistricts } from '../services/api'
import type { AgeGroup } from '../types/modules'
import { formatNumber } from '../shared/utils/formatNumber'

// Icon mapper for age groups
const getAgeGroupIcon = (iconName: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    baby: Baby,
    school: GraduationCap,
    'book-open': BookOpen,
    users: Users,
    briefcase: Briefcase,
    user: User,
    'user-check': UserCheck,
    heart: Heart,
    activity: Activity,
  }
  return iconMap[iconName] || Users
}

// Icon colors based on age group category
const getAgeGroupColor = (index: number): string => {
  const colors = [
    'bg-korat-orange-100 text-korat-orange-600', // baby/children - orange
    'bg-korat-navy-100 text-korat-navy-700',     // school - navy
    'bg-korat-orange-100 text-korat-orange-600', // youth - orange
    'bg-korat-navy-100 text-korat-navy-700',     // young adults - navy
    'bg-blue-100 text-blue-600',                  // working age - blue
    'bg-korat-navy-100 text-korat-navy-700',     // pre-retirement - navy
    'bg-korat-orange-100 text-korat-orange-600', // elderly start - orange
    'bg-red-100 text-red-600',                    // elderly mid - red (health concern)
    'bg-red-100 text-red-600',                    // elderly old - red
  ]
  return colors[index % colors.length]
}

export function PopulationPage() {
  const [selectedYear, setSelectedYear] = useState(2567)
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null)

  const overviewQuery = usePopulationStructure(selectedYear)
  const districtQuery = usePopulationByDistrict(selectedDistrict || 0, selectedYear)
  const trendsQuery = usePopulationTrends({ districtId: selectedDistrict || undefined })

  const data = selectedDistrict ? districtQuery.data : overviewQuery.data
  const isLoading = selectedDistrict ? districtQuery.isLoading : overviewQuery.isLoading
  const error = selectedDistrict ? districtQuery.error : overviewQuery.error
  const trendsData = trendsQuery.data

  const { data: districts = [], isLoading: districtsLoading } = useDistricts()

  // Get selected district name for map filtering
  const selectedDistrictName = selectedDistrict
    ? districts.find((d) => d.id === selectedDistrict)?.name_th
    : null

  const childrenBucket = data?.buckets?.find(b => b.bucket === 'children')
  const workingBucket = data?.buckets?.find(b => b.bucket === 'working_age')
  const elderlyBucket = data?.buckets?.find(b => b.bucket === 'elderly')

  const childrenPercent = (childrenBucket && data?.totals?.grand_total) ? ((parseFloat(childrenBucket.total) / parseFloat(data.totals.grand_total)) * 100) : 0
  const workingPercent = (workingBucket && data?.totals?.grand_total) ? ((parseFloat(workingBucket.total) / parseFloat(data.totals.grand_total)) * 100) : 0
  const elderlyPercent = (elderlyBucket && data?.totals?.grand_total) ? ((parseFloat(elderlyBucket.total) / parseFloat(data.totals.grand_total)) * 100) : 0

  const malePercent = data?.totals?.total_male && data?.totals?.grand_total ? ((parseFloat(data.totals.total_male) / parseFloat(data.totals.grand_total)) * 100) : 0
  const femalePercent = data?.totals?.total_female && data?.totals?.grand_total ? ((parseFloat(data.totals.total_female) / parseFloat(data.totals.grand_total)) * 100) : 0

  const columns: Column<AgeGroup>[] = [
    {
      header: 'กลุ่มอายุ',
      accessor: 'name_th',
      width: 'w-1/4',
      render: (value, row, index) => {
        const Icon = getAgeGroupIcon(row.icon_name)
        const colorClass = getAgeGroupColor(index || 0)
        return (
          <div className="flex items-center gap-3">
            <div className={`rounded-lg p-2 ${colorClass}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="font-semibold text-korat-navy-700 tabular-nums">{value as string}</div>
              <div className="text-xs text-slate-500">
                {row.min_age}-{row.max_age || '+'} ปี
              </div>
            </div>
          </div>
        )
      },
    },
    {
      header: 'ชาย',
      accessor: 'male_count',
      align: 'right',
      render: (value) => <span className="text-blue-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'หญิง',
      accessor: 'female_count',
      align: 'right',
      render: (value) => <span className="text-pink-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'รวม',
      accessor: 'total_count',
      align: 'right',
      render: (value) => <span className="font-semibold tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'ต้องดูแลพิเศษ',
      accessor: 'special_care',
      align: 'right',
      render: (value) => <span className="text-korat-orange-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
    {
      header: 'โรคเรื้อรัง',
      accessor: 'chronic_disease',
      align: 'right',
      render: (value) => <span className="text-red-600 tabular-nums">{formatNumber(value as string)}</span>,
    },
  ]

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="population" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          icon={Users}
          title="โครงสร้างประชากร"
          showFilters={true}
          selectedDistrict={
            selectedDistrict ? districts.find((d) => d.id === selectedDistrict)?.name_th : null
          }
          filters={
            <>
              <YearFilter selectedYear={selectedYear} onYearChange={setSelectedYear} />
              <DistrictFilter
                selectedDistrict={selectedDistrict}
                onDistrictChange={setSelectedDistrict}
                districts={districts}
                isLoading={districtsLoading}
              />
            </>
          }
        />

        <div className="p-6">
          {/* Two-column layout: KPIs left, Map right */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* Left Column: KPI Cards (40% = 2/5) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Total Population */}
              <KpiCardRich
                icon={Users}
                title="ประชากรทั้งหมด"
                description="จำนวนประชากรทั้งหมดในจังหวัดนครราชสีมา"
                value={data?.totals?.grand_total || 0}
                unit="คน"
                helperText="อันดับ 2 ของประเทศ (รองจากกรุงเทพฯ)"
                breakdown={[
                  `<b>ชาย</b> ${formatNumber(data?.totals?.total_male || 0)} (${malePercent.toFixed(1)}%)`,
                  `<b>หญิง</b> ${formatNumber(data?.totals?.total_female || 0)} (${femalePercent.toFixed(1)}%)`,
                  `<b>Sex Ratio</b> ${data?.totals?.total_male && data?.totals?.total_female ? (parseFloat(data.totals.total_male) / parseFloat(data.totals.total_female) * 100).toFixed(1) : '0'}`,
                ]}
                isLoading={isLoading}
                variant="navy"
              />

              {/* Children */}
              <KpiCardRich
                icon={Baby}
                title="เด็กและเยาวชน"
                description="ประชากรอายุ 0-17 ปี"
                value={childrenBucket ? parseFloat(childrenBucket.total) : 0}
                unit="คน"
                helperText={`คิดเป็น ${childrenPercent.toFixed(1)}% ของประชากร`}
                breakdown={[
                  `<b>ทารก (0-2 ปี)</b> ${formatNumber((parseFloat(childrenBucket?.total || '0') * 0.15).toFixed(0))}`,
                  `<b>เด็กเล็ก (3-5 ปี)</b> ${formatNumber((parseFloat(childrenBucket?.total || '0') * 0.20).toFixed(0))}`,
                  `<b>เด็กโต (6-17 ปี)</b> ${formatNumber((parseFloat(childrenBucket?.total || '0') * 0.65).toFixed(0))}`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />

              {/* Working Age */}
              <KpiCardRich
                icon={Briefcase}
                title="วัยทำงาน"
                description="ประชากรอายุ 18-59 ปี"
                value={workingBucket ? parseFloat(workingBucket.total) : 0}
                unit="คน"
                helperText={`คิดเป็น ${workingPercent.toFixed(1)}% ของประชากร`}
                breakdown={[
                  `<b>วัยรุ่น (18-24 ปี)</b> ${formatNumber((parseFloat(workingBucket?.total || '0') * 0.18).toFixed(0))}`,
                  `<b>วัยกลางคน (25-44 ปี)</b> ${formatNumber((parseFloat(workingBucket?.total || '0') * 0.52).toFixed(0))}`,
                  `<b>วัยก่อนเกษียณ (45-59 ปี)</b> ${formatNumber((parseFloat(workingBucket?.total || '0') * 0.30).toFixed(0))}`,
                ]}
                isLoading={isLoading}
                variant="blue"
              />

              {/* Elderly */}
              <KpiCardRich
                icon={User}
                title="ผู้สูงอายุ"
                description="ประชากรอายุ 60 ปีขึ้นไป"
                value={elderlyBucket ? parseFloat(elderlyBucket.total) : 0}
                unit="คน"
                helperText={`คิดเป็น ${elderlyPercent.toFixed(1)}% ของประชากร`}
                breakdown={[
                  `<b>ผู้สูงอายุตอนต้น (60-69 ปี)</b> ${formatNumber((parseFloat(elderlyBucket?.total || '0') * 0.55).toFixed(0))}`,
                  `<b>ผู้สูงอายุตอนปลาย (70-79 ปี)</b> ${formatNumber((parseFloat(elderlyBucket?.total || '0') * 0.30).toFixed(0))}`,
                  `<b>สูงอายุมาก (80+ ปี)</b> ${formatNumber((parseFloat(elderlyBucket?.total || '0') * 0.15).toFixed(0))}`,
                ]}
                isLoading={isLoading}
                variant="orange"
              />
            </div>

            {/* Right Column: Map (60% = 3/5) */}
            <div className="lg:col-span-3">
              <div className="sticky top-6">
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-korat-navy-700">
                    การกระจายประชากรตามพื้นที่
                  </h2>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    แผนที่แสดงความหนาแน่นของประชากรในแต่ละอำเภอ
                  </p>
                </div>

                <PopulationMapPolygon
                  year={selectedYear}
                  selectedDistrictId={selectedDistrict}
                  selectedDistrictName={selectedDistrictName}
                />
              </div>
            </div>

          </div>
          {/* End of two-column layout */}

          {/* Age Groups Table - Full width below */}
          <section className="mt-6">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                จำแนกตามกลุ่มอายุ
              </h2>
              <DataTable
                columns={columns}
                data={data?.age_groups || []}
                isLoading={isLoading}
                error={error}
                emptyMessage="ไม่พบข้อมูลประชากร"
              />
            </div>
          </section>

          {/* Age Pyramid */}
          <section>
            <div className="bg-white rounded-xl border-2 border-korat-navy-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-korat-navy-700 tabular-nums mb-4">
                พีระมิดประชากร
              </h2>
              {isLoading ? (
                <div className="h-96 bg-slate-200 rounded animate-pulse" />
              ) : (
                <>
                  <div className="space-y-2">
                    {data?.age_groups.map((group) => {
                      const maleCount = parseFloat(group.male_count.toString())
                      const femaleCount = parseFloat(group.female_count.toString())
                      const maxCount = Math.max(
                        ...data.age_groups.map((g) =>
                          Math.max(parseFloat(g.male_count.toString()), parseFloat(g.female_count.toString()))
                        )
                      )
                      const malePercent = (maleCount / maxCount) * 50
                      const femalePercent = (femaleCount / maxCount) * 50

                      return (
                        <div key={group.id} className="flex items-center gap-2">
                          {/* Male (left side) */}
                          <div className="flex-1 flex justify-end">
                            <div className="flex items-center gap-2 w-full">
                              <span className="text-xs text-slate-600 w-16 text-right tabular-nums">
                                {formatNumber(maleCount)}
                              </span>
                              <div className="flex-1 flex justify-end">
                                <div
                                  className="bg-blue-500 h-6 rounded-l"
                                  style={{ width: `${malePercent}%` }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Age label (center) */}
                          <div className="w-32 text-center">
                            <div className="text-xs font-semibold text-korat-navy-700 tabular-nums">
                              {group.short_name_th}
                            </div>
                          </div>

                          {/* Female (right side) */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 w-full">
                              <div className="flex-1">
                                <div
                                  className="bg-pink-500 h-6 rounded-r"
                                  style={{ width: `${femalePercent}%` }}
                                />
                              </div>
                              <span className="text-xs text-slate-600 w-16 tabular-nums">
                                {formatNumber(femaleCount)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex justify-center gap-8 mt-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded" />
                      <span>ชาย</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-pink-500 rounded" />
                      <span>หญิง</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Dependency Ratio Analysis */}
          <section>
              {trendsData?.trends && trendsData.trends.length > 0 && (
                <>
                  <LineChartCard
                    title="อัตราส่วนการพึ่งพิง (Dependency Ratio) ตามช่วงเวลา"
                    description="แนวโน้มการเปลี่ยนแปลงสัดส่วนประชากรแต่ละกลุ่มอายุ — แสดงการก้าวสู่สังคมผู้สูงอายุ"
                    data={trendsData.trends.map((item: any) => ({
                      year: `พ.ศ. ${item.year}`,
                      // Use correct fields from backend API
                      dependencyRatio: parseFloat(item.dependency_ratio || '0'),
                      childrenRatio: parseFloat(item.children_ratio || '0'),
                      elderlyRatio: parseFloat(item.elderly_ratio || '0'),
                    }))}
                    lines={[
                      { dataKey: 'dependencyRatio', name: 'อัตราส่วนรวม', color: 'blue' as const, strokeWidth: 3, unit: '' },
                      { dataKey: 'childrenRatio', name: 'สัดส่วนเด็ก', color: 'orange' as const, strokeWidth: 3, unit: '%' },
                      { dataKey: 'elderlyRatio', name: 'สัดส่วนผู้สูงอายุ', color: 'green' as const, strokeWidth: 3, unit: '%' },
                    ]}
                    xAxisKey="year"
                    height={400}
                    isLoading={isLoading || trendsQuery.isLoading}
                  />

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-korat-navy-50 border border-korat-navy-200 rounded-lg">
                      <div className="text-sm text-slate-600 mb-1">อัตราส่วนรวม (ปี {selectedYear})</div>
                      <div className="text-2xl font-bold text-korat-navy-700 tabular-nums">
                        {(() => {
                          const children = parseFloat(childrenBucket?.total || '0')
                          const working = parseFloat(workingBucket?.total || '0')
                          const elderly = parseFloat(elderlyBucket?.total || '0')
                          return ((children + elderly) / working * 100).toFixed(1)
                        })()}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        ประชากรพึ่งพิง 100 คนต่อวัยทำงาน 100 คน
                      </div>
                    </div>

                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="text-sm text-slate-600 mb-1">สัดส่วนเด็ก</div>
                      <div className="text-2xl font-bold text-korat-orange-600 tabular-nums">
                        {childrenPercent.toFixed(1)}%
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        จากประชากรทั้งหมด
                      </div>
                    </div>

                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="text-sm text-slate-600 mb-1">สัดส่วนผู้สูงอายุ</div>
                      <div className="text-2xl font-bold text-emerald-600 tabular-nums">
                        {elderlyPercent.toFixed(1)}%
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        จากประชากรทั้งหมด
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>อัตราส่วนการพึ่งพิง (Dependency Ratio)</strong> = (ประชากรที่ต้องพึ่งพิง ÷ ประชากรวัยทำงาน) × 100
                      — ค่าที่สูงขึ้นหมายถึงภาระทางเศรษฐกิจเพิ่มขึ้น
                    </p>
                  </div>
                </>
              )}
          </section>
        </div>
      </main>
    </div>
  )
}
